import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BookingAction, SearchStatus, UserRolePermissions } from '../../../../../constants';
import TwTextField from '../../../../TwTextField';
import validateEmail from '../../../../../helpers/validationHelpers/validateEmail';
import CardSelectForm from './CardSelectForm';
import validateCreditCards from '../../../../../helpers/validationHelpers/validateCreditCards';
import getTripModes from '../../../../../helpers/getTripModes';
import { changeValidation, doUseSavedCardForCheckout, selectedCreditCard } from '../../../../../store/slices/trip';
import Checkbox from '../../../../Checkbox';
import { checkSitePermission } from '../../../../../store/slices/user';
import getAcceptedPaymentCards from '../../../../../helpers/getAcceptedPaymentCards';
import { usePayment } from '../../../../CruiseBooking/PaymentConfig/AddCardForm/VgsIFrameInputProvider';

const EmailTripQuoteForm = ({
  saveSendTripEmailStatus, setOpenEmailInput, saveSendTripEmailError, onBookClick,
  tripId,
}) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [emailValidated, setEmailValidated] = useState(false);
  const creditCards = useSelector((store) => store.trip.creditCards);
  const [doShowCardForm, setDoShowCardForm] = useState(false);
  const trip = useSelector((state) => state.trip.selectedTrips && state.trip.selectedTrips[tripId]);
  const { chargeableItemOptions } = useSelector((state) => state.trip);
  const doShowCheckboxToSelfBook = useSelector(checkSitePermission(
    UserRolePermissions.canBookFromTripQuote,
  ));
  const {
    availableStates, travelAgency, isLoggedIn,
  } = useSelector((store) => store.user);
  const [doAllowSelfBookingTripQuote, setDoAllowSelfBookingTripQuote] = useState((
    !isLoggedIn ? true : (travelAgency
     && travelAgency.permissions
     && travelAgency.permissions.anonymous
     && travelAgency.permissions.anonymous
       .indexOf(UserRolePermissions.canBookFromTripQuote)) > -1 || false));

  const selectedCardForCheckout = useSelector(selectedCreditCard);
  const doUseSavedCardForBooking = useSelector(doUseSavedCardForCheckout);
  const cardIndex = 0;
  const payment = usePayment(cardIndex);
  const { tokenizeFunc, isFormValid } = payment || {};

  const acceptedPaymentCards = useMemo(() => {
    if (!trip?.hotelItineraries) {
      return {};
    }
    const hotelItineraries = Array.isArray(trip.hotelItineraries)
      ? trip.hotelItineraries
      : Object.values(trip.hotelItineraries);
    if (!hotelItineraries.length) {
      return {};
    }
    return getAcceptedPaymentCards(hotelItineraries);
  }, [trip]);

  const doAllowSelectPaymentMethod = useMemo(() => {
    if (!travelAgency || !travelAgency.useCardFromAgencyProfile) return false;
    if (!doAllowSelfBookingTripQuote) return false;
    // For now not allowing to select card when cruise itinerary.
    // To allow it we need to display address fields and cvv. Currently there is no way to save CVV
    if (trip && trip.cruiseItinerary) return false;
    const { itinerariesToBookWithPaymentMethod } = getTripModes(trip, travelAgency, false);
    return itinerariesToBookWithPaymentMethod && itinerariesToBookWithPaymentMethod.length > 0;
  }, [travelAgency, trip, doAllowSelfBookingTripQuote]);

  const handleBookClick = () => {
    if (doAllowSelectPaymentMethod && doShowCardForm) {
      const [
        isCreditCardValid,
        creditCardsFormValidations,
        isSavedCardValidated,
      ] = validateCreditCards(false, false, creditCards, availableStates, true,
        doUseSavedCardForBooking, selectedCardForCheckout, acceptedPaymentCards,
        chargeableItemOptions, isFormValid ? tokenizeFunc : null);
      if (!isCreditCardValid) {
        dispatch(changeValidation({
          creditCardsValidations: creditCardsFormValidations,
          isSavedCardValidated,
        }));
      }
    }
    const isValidEmail = validateEmail(email);
    if (isValidEmail && (isFormValid || !doShowCardForm)) {
      onBookClick(BookingAction.SEND_QUOTE, email, null,
        doAllowSelectPaymentMethod && doShowCardForm, doAllowSelfBookingTripQuote,
        undefined, undefined, tokenizeFunc, isFormValid);
    } if (!isValidEmail) {
      setEmailValidated(true);
    }
  };

  const handleEmailChange = (event) => {
    setEmail(event.currentTarget.value);
    setEmailValidated(false);
  };

  const handleShowCardForm = (val) => {
    setDoShowCardForm(val);
  };

  useEffect(() => {
    if (saveSendTripEmailStatus === SearchStatus.DONE && !saveSendTripEmailError) {
      setEmail('');
      setEmailValidated(false);
      setDoShowCardForm(false);
    }
  }, [saveSendTripEmailError, saveSendTripEmailStatus]);

  return (
    <div
      className="grid grid-flow-row w-full"
      style={doShowCardForm ? { width: '25vw' } : undefined}
    >
      <div className="mt-1 border-theme">
        <TwTextField label="Send email quote to..." value={email} variant="outlined" className="w-full block" error={emailValidated} helperText={emailValidated && 'Valid email required.'} InputProps={{ className: 'border-theme' }} onChange={handleEmailChange} autoFocus size="small" />
        {isLoggedIn && doShowCheckboxToSelfBook ? (
          <div className="text-sm mt-2">
            <Checkbox checked={doAllowSelfBookingTripQuote} labelStyles={{ whiteSpace: 'break-spaces' }} onChange={(event, newValue) => setDoAllowSelfBookingTripQuote(newValue)} label={`Allow ${validateEmail(email) ? email : 'client'} to self-book from this quote`} />
          </div>
        ) : <></>}
        {doAllowSelectPaymentMethod ? (
          <CardSelectForm
            tripId={tripId}
            onCardFormShown={handleShowCardForm}
            doShowCardForm={doShowCardForm}
          />
        ) : <></>}
      </div>
      <div className="mt-2">
        {saveSendTripEmailStatus === SearchStatus.SEARCHING
          ? <div className="text-right"><CircularProgress color="secondary" size={15} /></div>
          : (
            <div className="text-xs text-right">
              <button type="button" className="mr-4" onClick={() => setOpenEmailInput(false)}>Cancel</button>
              <button type="button" className="bg-button-theme rounded-md py-2 px-4 text-button-theme font-bold" onClick={handleBookClick}>Submit</button>
              {saveSendTripEmailStatus === SearchStatus.DONE && !saveSendTripEmailError
                          && (
                            <span className="ml-2">
                              <DoneAllIcon fontSize="small" className="text-green-500" />
                              {' '}
                              Sent!
                            </span>
                          )}
            </div>
          )}
        {saveSendTripEmailStatus === SearchStatus.DONE && saveSendTripEmailError && isFormValid ? <div className="text-red-600 text-sm">{saveSendTripEmailError}</div> : null}
      </div>
    </div>
  );
};

EmailTripQuoteForm.propTypes = {
  saveSendTripEmailStatus: PropTypes.number.isRequired,
  setOpenEmailInput: PropTypes.func.isRequired,
  saveSendTripEmailError: PropTypes.string,
  onBookClick: PropTypes.func.isRequired,
  tripId: PropTypes.string.isRequired,
};

EmailTripQuoteForm.defaultProps = {
  saveSendTripEmailError: '',
};

export default EmailTripQuoteForm;
